/**
 * Hooks dashboard HomePage (S6 PR5).
 *
 * 4 useQuery indépendants → isolation des pannes (si KPIs lèvent 500,
 * AlertBar continue de fonctionner). Aucun agrégat unique côté BE par
 * design (cf. dashboard_service docstring).
 *
 * - `useDashboardAlerts` : tokens 30/15/7j + queue failed + polling failed
 *   + scheduler stalled.
 * - `useDashboardKpis` : sales 30j, listings actifs, queue size, favorites.
 * - `useDashboardActivity` : flux audit_log filtré + redacté.
 * - `useDashboardTopTags` : délégation pure à `/api/signals/top-tags`.
 *
 * Refetch interval : 60s pour les 4 (HomePage est "vivante" — KPIs
 * mis à jour automatiquement quand l'user reste dessus).
 */

import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/utils/api/client'
import type { components } from '@/types/api-generated'

export type Alert = components['schemas']['Alert']
export type KpisResponse = components['schemas']['KpisResponse']
export type ActivityEntry = components['schemas']['ActivityEntry']
export type TopTagEntry = components['schemas']['TopTagEntry']

const REFETCH_INTERVAL_MS = 60_000

export const dashboardKeys = {
  all: ['dashboard'] as const,
  alerts: () => [...dashboardKeys.all, 'alerts'] as const,
  kpis: () => [...dashboardKeys.all, 'kpis'] as const,
  activity: (limit: number) => [...dashboardKeys.all, 'activity', limit] as const,
  topTags: (limit: number) => [...dashboardKeys.all, 'top-tags', limit] as const,
}

export function useDashboardAlerts() {
  return useQuery<Alert[]>({
    queryKey: dashboardKeys.alerts(),
    queryFn: async () => {
      const { data } = await apiClient.get<Alert[]>('/dashboard/alerts')
      return data
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  })
}

export function useDashboardKpis() {
  return useQuery<KpisResponse>({
    queryKey: dashboardKeys.kpis(),
    queryFn: async () => {
      const { data } = await apiClient.get<KpisResponse>('/dashboard/kpis')
      return data
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  })
}

export function useDashboardActivity(limit: number = 50) {
  return useQuery<ActivityEntry[]>({
    queryKey: dashboardKeys.activity(limit),
    queryFn: async () => {
      const { data } = await apiClient.get<ActivityEntry[]>('/dashboard/recent-activity', {
        params: { limit },
      })
      return data
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  })
}

export function useDashboardTopTags(limit: number = 5) {
  return useQuery<TopTagEntry[]>({
    queryKey: dashboardKeys.topTags(limit),
    queryFn: async () => {
      const { data } = await apiClient.get<TopTagEntry[]>('/signals/top-tags', {
        params: { platform: 'etsy', limit },
      })
      return data
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  })
}
